import React, { useMemo, useState } from 'react';
import {
    Paper,
    Typography,
    IconButton,
    TextField,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { ProductsTabProps } from '../ProductTypes';

const ProductsTab: React.FC<ProductsTabProps> = ({
    products,
    onEditProduct,
    onDeleteProduct,
    onAddNewProduct
}) => {
    const [searchQuery, setSearchQuery] = useState('');

    const sortedProducts = useMemo(() => {
        return [...products].filter(product => 
            product.productName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [products, searchQuery]);

    return (
        <div className="space-y-6">
            {/* Sorting and Filtering Controls */}
            <div className="flex justify-between items-center">
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Products & Services
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <SearchIcon sx={{ 
                                color: 'text.secondary',
                                fontSize: 20,
                                mr: 1
                            }} />
                        ),
                    }}
                    sx={{
                        width: '300px',
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: '10px',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: '#f8f9fa',
                                '& fieldset': {
                                    borderColor: 'primary.main',
                                }
                            },
                            '&.Mui-focused': {
                                '& fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: '2px',
                                },
                                backgroundColor: '#f8f9fa'
                            }
                        },
                        '& input::placeholder': {
                            color: 'text.secondary',
                            opacity: 0.8
                        }
                    }}
                />
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
                {/* Add Product Card */}
                <Paper
                    elevation={0}
                    sx={{
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                        height: '100%',
                        minHeight: '360px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: 'grey.50',
                            transform: 'translateY(-2px)',
                            boxShadow: 1
                        }
                    }}
                    onClick={onAddNewProduct}
                >
                    <div className="text-center p-6">
                        <AddIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                        <Typography sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>
                            Add New Product
                        </Typography>
                    </div>
                </Paper>

                {/* Product Cards */}
                {sortedProducts.map((product) => (
                    <Paper
                        key={product.id}
                        elevation={0}
                        sx={{
                            border: '1px solid #E5E7EB',
                            borderRadius: 2,
                            overflow: 'visible',
                            position: 'relative',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 1
                            }
                        }}
                    >
                        <div className="relative h-48">
                            <img
                                src={product.productImages?.[0] || '/placeholder.png'}
                                alt={product.productName}
                                className="w-full h-full object-cover"
                            />
                            <div 
                                className={`absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-medium ${
                                    product.isLive 
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}
                                style={{ zIndex: 2 }}
                            >
                                {product.isLive ? 'Live' : 'Draft'}
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="text-lg font-semibold text-gray-900 flex-grow">
                                    {product.productName}
                                </h3>
                                <div className="flex gap-2">
                                    <IconButton
                                        size="small"
                                        onClick={() => onEditProduct(product)}
                                        sx={{
                                            bgcolor: 'background.paper',
                                            boxShadow: 1,
                                            '&:hover': { bgcolor: 'grey.100' }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => onDeleteProduct(product.id)}
                                        sx={{
                                            bgcolor: 'background.paper',
                                            boxShadow: 1,
                                            '&:hover': { bgcolor: 'error.light', color: 'white' }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                                {product.description}
                            </p>
                            <div className="flex justify-between items-center mb-3">
                                <div>
                                    <span className="text-lg font-bold text-gray-900">
                                        ₹{product.basePrice}
                                    </span>
                                    {product.variants && product.variants.length > 0 && (
                                        <span className="text-sm text-gray-500 ml-2">
                                            +{product.variants.length} variants
                                        </span>
                                    )}
                                </div>
                                <span className="text-sm text-gray-500">
                                    Base Stock: {product.remainingInventory}
                                </span>
                            </div>
                            
                            {/* Dynamic Variants Display */}
                            {product.variants && product.variants.length > 0 && (
                                <div className="mt-4 space-y-4">
                                    <div className="border-t pt-3">
                                        <span className="text-sm font-medium text-gray-700">
                                            Variants:
                                        </span>
                                        <div className="mt-2 space-y-2">
                                            {product.variants.map((variant) => (
                                                <div 
                                                    key={variant.id} 
                                                    className="flex justify-between items-center bg-gray-50 p-2 rounded"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {/* Display all combinations */}
                                                        {Object.entries(variant.combination).map(([key, value]) => (
                                                            <span key={key} className="text-sm text-gray-600">
                                                                {key}: {
                                                                    key === 'color' 
                                                                        ? <div
                                                                            className="inline-block w-4 h-4 rounded-full border border-gray-200 align-middle ml-1"
                                                                            style={{ backgroundColor: value }}
                                                                            title={value}
                                                                          />
                                                                        : value
                                                                }
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-sm font-medium">
                                                            ₹{variant.price}
                                                        </span>
                                                        <span className="text-xs text-gray-500">
                                                            Stock: {variant.remainingInventory}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Paper>
                ))}
            </div>
        </div>
    );
};

export default ProductsTab; 